import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { s3Service } from '@/services/s3';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Dialog,
  Spinner,
  Timeline,
} from '@noorden-insurtech/alba-design-system';
import { Download, FileText, Folder, UserRound } from 'lucide-react';
import { useProposalAnalysisInteractions } from '../../hooks';
import type {
  ProposalAnalysisAttachment,
  ProposalAnalysisInteractionAction,
} from '../../types';

/**
 * Mapa de ações conhecidas (constantes do BFF em `ProposalAnalysisAction`)
 * para o título exibido na timeline. Strings desconhecidas caem no fallback
 * `record.action`.
 */
const ACTION_LABELS: Record<string, string> = {
  SUBMITTED_FOR_ANALYSIS: 'Análise Criada',
  OBSERVATION: 'Observação',
  'Proposta Aprovada na Análise': 'Proposta Aprovada na Análise',
  'Proposta Recusada na Análise': 'Proposta Recusada na Análise',
};

const STATUS_LABELS: Record<string, string> = {
  [String(PortalStatus.ProposalInAnalysis)]: 'Em análise',
  [String(PortalStatus.ProposalTransmitted)]: 'Aceita',
  [String(PortalStatus.QuoteClosed)]: 'Recusada',
};

function mapCreatedAt(createdAt: string) {
  const date = new Date(createdAt);
  return {
    day: date.getDate(),
    month: date.toLocaleString('pt-BR', { month: 'short' }).toUpperCase(),
    time: date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

function resolveActionLabel(action: ProposalAnalysisInteractionAction): string {
  return ACTION_LABELS[action] ?? action;
}

interface ProposalInteractionsTimelineProps {
  proposalId: string;
}

export function ProposalInteractionsTimeline({
  proposalId,
}: ProposalInteractionsTimelineProps) {
  const { data: records, isLoading, error } = useProposalAnalysisInteractions(
    proposalId
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-6'>
        <Spinner size='md' colorScheme='blue' />
      </div>
    );
  }

  if (error) {
    return (
      <p className='font-montserrat text-sm text-alba-error-red-500'>
        Erro ao carregar interações da análise. Tente novamente.
      </p>
    );
  }

  const items = (records ?? []).map(record => {
    const createdAt = mapCreatedAt(record.createdAt);
    return {
      id: record.id,
      day: createdAt.day,
      month: createdAt.month,
      time: createdAt.time,
      title: resolveActionLabel(record.action),
      status: record.status,
      description: record.description,
      rejectionReason: record.rejectionReason,
      attachments: record.attachments,
      author: record.userName,
    };
  });

  return (
    <Timeline.Root>
      <Timeline.Heading className='text-alba-black-500 text-base'>
        Registro de interações
      </Timeline.Heading>

      {items.length > 0 && (
        <Timeline.List>
          {items.map(entry => (
            <Timeline.Item key={entry.id}>
              <Timeline.Date
                day={entry.day}
                month={entry.month}
                time={entry.time}
              />
              <Timeline.Marker />

              <Timeline.Card size='full'>
                <Timeline.CardTitle>{entry.title}</Timeline.CardTitle>

                <Timeline.CardDivider />

                <Timeline.CardBody>
                  {entry.status && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      <span className='font-medium'>Status:</span>{' '}
                      {STATUS_LABELS[entry.status] ?? entry.status}
                    </p>
                  )}

                  {entry.rejectionReason && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      <span className='font-medium'>Motivo da recusa:</span>{' '}
                      {entry.rejectionReason}
                    </p>
                  )}

                  {entry.description && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      {entry.description}
                    </p>
                  )}

                  {entry.attachments.length > 0 && (
                    <div className='pt-1'>
                      <AttachmentsDialog attachments={entry.attachments} />
                    </div>
                  )}
                </Timeline.CardBody>

                <Timeline.CardFooter>
                  <UserRound size={16} className='text-alba-gray-600' />
                  <span className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                    Alterado por {entry.author}
                  </span>
                </Timeline.CardFooter>
              </Timeline.Card>
            </Timeline.Item>
          ))}
        </Timeline.List>
      )}

      {items.length === 0 && (
        <div className='flex flex-col items-center justify-center'>
          <p className='font-montserrat text-sm text-alba-gray-600'>
            Nenhuma interação encontrada para esta análise.
          </p>
        </div>
      )}
    </Timeline.Root>
  );
}

async function downloadAttachment(attachment: ProposalAnalysisAttachment) {
  try {
    const url = await s3Service.fetchPresignedDownloadUrl(attachment.keyPath);
    await downloadFromSignedUrl(url, attachment.name);
  } catch (error) {
    toaster.create({
      type: 'error',
      title: 'Erro ao baixar anexo',
      description:
        error instanceof Error ? error.message : 'Tente novamente em instantes.',
    });
  }
}

function AttachmentsDialog({
  attachments,
}: {
  attachments: ProposalAnalysisAttachment[];
}) {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button variant='secondary' size='md'>
          Visualizar arquivos anexados ({attachments.length})
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='w-full max-w-md pb-2'>
            <Dialog.Header className='flex flex-row items-center justify-between'>
              <Dialog.Title className='text-lg'>
                <Folder className='text-alba-gray-600 size-6' />
                Arquivos anexados
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Dialog.CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body className='flex flex-col gap-4'>
              {attachments.map(attachment => (
                <button
                  key={attachment.id}
                  type='button'
                  onClick={() => void downloadAttachment(attachment)}
                  className='flex flex-row items-center justify-start gap-2 px-3 py-2 border border-alba-gray-400 rounded-lg text-left hover:border-alba-blue-500 cursor-pointer'
                >
                  <FileText className='text-alba-black-500 size-6 shrink-0' />
                  <div className='flex-1 flex flex-col items-start justify-start'>
                    <p className='font-montserrat text-sm text-alba-black-500'>
                      {attachment.name}
                    </p>
                    <p className='font-montserrat text-xs font-normal text-alba-gray-600'>
                      {attachment.documentType}
                    </p>
                  </div>
                  <Download className='text-alba-blue-600 size-4' />
                </button>
              ))}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
